import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
  candidateDetailsSelectors,
  clearCandidateDetails,
  fetchCandidateDetails,
} from '@redux/candidateDetails';
import { candidateDetailsPath, isStatusLoading } from '@utils';
import { Loader } from 'components/common/Loader/Loader';
import {
  CandidateSection,
  ClosedBanner,
  getCandidateSections,
} from '../CandidateDetailSections/CandidateDetailSections';
import { CandidateDetailsActionButton } from '../CandidaterDetailsActionButton/CandidaterDetailsActionButton';
import { CandidateDetailsHeader } from '../CandidateDetailsHeader/CandidateDetailsHeader';
import { DetailsDrawer } from './DetailsDrawer';

interface ICandidateDrawerProps {
  candidateId: string | null;
  onClose: () => void;
}

const DrawerBody: React.FC = () => {
  const { apiStatus, data } = useAppSelector(
    candidateDetailsSelectors.getCandidateDetailsData,
  );

  if (isStatusLoading(apiStatus) || !data) return <Loader />;

  return (
    <Box pb={12}>
      <Box px={3} pt={3} sx={{ '&:empty': { display: 'none' } }}>
        <ClosedBanner data={data} />
      </Box>
      {getCandidateSections(data).map((section) => (
        <CandidateSection key={section.title} title={section.title}>
          {section.content}
        </CandidateSection>
      ))}
    </Box>
  );
};

export const CandidateDrawer: React.FC<ICandidateDrawerProps> = ({
  candidateId,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { data } = useAppSelector(
    candidateDetailsSelectors.getCandidateDetailsData,
  );

  useEffect(() => {
    if (!candidateId) return undefined;

    dispatch(fetchCandidateDetails(candidateId));

    return () => {
      dispatch(clearCandidateDetails());
    };
  }, [candidateId, dispatch]);

  return (
    <DetailsDrawer
      open={Boolean(candidateId)}
      heading={<CandidateDetailsHeader data={data} variant="drawer" />}
      actions={data && <CandidateDetailsActionButton data={data} />}
      onClose={onClose}
      onOpenFullPage={() =>
        candidateId && navigate(candidateDetailsPath(candidateId, search))
      }
    >
      {candidateId && <DrawerBody />}
    </DetailsDrawer>
  );
};
